import { useEffect, useRef } from 'react'

interface EngagementEvent {
  type: string
  value?: number | string
  timestamp: number
  path: string
}

/**
 * Tracks user engagement signals to fight 100% bounce rate.
 * Fires events for: scroll depth milestones, time on page, click interactions.
 * Each meaningful event marks the visit as "engaged" (non-bounce).
 */
export function useEngagementTracking() {
  const sessionStartRef = useRef<number>(Date.now())
  const milestonesHitRef = useRef<Set<string>>(new Set())
  const interactionCountRef = useRef<number>(0)

  useEffect(() => {
    const logEvent = (event: EngagementEvent) => {
      // Console log so it's visible in analytics/devtools
      console.log('engagement_event:', event)

      // Push to Google Analytics if available (gtag)
      const w = window as any
      if (typeof w.gtag === 'function') {
        w.gtag('event', event.type, {
          event_category: 'engagement',
          event_label: event.path,
          value: event.value
        })
      }
      // Push to dataLayer for GTM if available
      if (Array.isArray(w.dataLayer)) {
        w.dataLayer.push({
          event: 'engagement',
          engagement_type: event.type,
          engagement_value: event.value,
          page_path: event.path
        })
      }
    }

    const fireMilestone = (key: string, type: string, value?: number | string) => {
      if (milestonesHitRef.current.has(key)) return
      milestonesHitRef.current.add(key)
      logEvent({ type, value, timestamp: Date.now(), path: window.location.pathname })
    }

    // Scroll depth tracking
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const percent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0

      ;[25, 50, 75, 90].forEach((m) => {
        if (percent >= m) fireMilestone(`scroll_${m}`, 'scroll_depth', m)
      })
    }

    // Time on page milestones
    const timeMilestones = [
      { ms: 15000, label: '15s' },
      { ms: 30000, label: '30s' },
      { ms: 60000, label: '60s' },
      { ms: 120000, label: '2m' }
    ]
    const timers = timeMilestones.map(({ ms, label }) =>
      window.setTimeout(() => fireMilestone(`time_${label}`, 'time_on_page', label), ms)
    )

    // Click tracking — counts as engagement after 1st click
    const handleClick = (e: MouseEvent) => {
      interactionCountRef.current += 1
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const text = target?.textContent?.trim().slice(0, 50) || ''
      const isCta = !!target?.closest('a, button')

      if (interactionCountRef.current === 1) {
        fireMilestone('first_click', 'first_interaction', tag)
      }
      if (isCta) {
        logEvent({
          type: 'cta_click',
          value: text,
          timestamp: Date.now(),
          path: window.location.pathname
        })
      }
    }

    // Page exit / unload — log total session time
    const handleBeforeUnload = () => {
      const duration = Math.round((Date.now() - sessionStartRef.current) / 1000)
      logEvent({
        type: 'session_end',
        value: duration,
        timestamp: Date.now(),
        path: window.location.pathname
      })
    }

    // Visibility change — log when user switches tabs
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        const duration = Math.round((Date.now() - sessionStartRef.current) / 1000)
        logEvent({
          type: 'tab_hidden',
          value: duration,
          timestamp: Date.now(),
          path: window.location.pathname
        })
      }
    }

    // Initial page view event
    fireMilestone('page_view', 'page_view', window.location.pathname)

    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('click', handleClick)
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('click', handleClick)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibility)
      timers.forEach(clearTimeout)
    }
  }, [])
}
